import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import type { Resource } from '__types__';
import { EntityType } from '__types__';
import type { EntitySchema } from 'v5/core/entity-schema/entitySchema.types';
import { encodeList, encodeUnit } from 'v5/core/entity-values/encodeUnit';
import type { ResourcePatch } from 'v5/core/entity-values/resourcePatch.types';
import type { CodecRegistry } from 'v5/core/entity-values/values.types';
import type { CreateFormValues } from 'v5/patterns/forms/schema-form/schemaForm.types';
import { emptyUnitValues } from './resourceBindings';

/** A resource with no rows: what create-mode encoders see as "current". */
export function emptyResource(resourceTypeId: number): Resource {
  return {
    id: 0,
    guid: '',
    entity_type: EntityType.CONTACT,
    full_name: '',
    resource_type_id: resourceTypeId,
    archived: false,
    user_id: null,
    created_at: new Date(0),
    updated_at: null,
    created_mechanism: null,
    locations: [],
    emails: [],
    websites: [],
    phone_numbers: [],
    socials: [],
    tags: [],
    groups: [],
    rates: [],
    custom_fields: [],
    custom_fieldgroups: [],
    bookings: [],
    resource_type: undefined as unknown as Resource['resource_type']
  };
}

export function initialCreateValues(
  schema: EntitySchema,
  codecs: CodecRegistry
): CreateFormValues<ResourceKinds> {
  const values: CreateFormValues<ResourceKinds> = {};
  for (const section of schema.sections) {
    for (const unit of section.units) {
      values[unit.id] =
        unit.type === 'list'
          ? { type: 'list', items: [] }
          : { type: 'fields', values: emptyUnitValues(unit.fields, codecs) };
    }
  }
  return values;
}

/** Create-mode submit: every unit becomes at most one patch; empty custom fields are skipped. */
export function createValuesToPatches(
  schema: EntitySchema,
  values: CreateFormValues<ResourceKinds>,
  codecs: CodecRegistry,
  resourceTypeId: number
): ResourcePatch[] {
  const current = emptyResource(resourceTypeId);
  const patches: ResourcePatch[] = [];
  for (const section of schema.sections) {
    for (const unit of section.units) {
      const unitValues = values[unit.id];
      if (!unitValues) continue;
      if (unit.type === 'list') {
        if (unitValues.type !== 'list' || unitValues.items.length === 0)
          continue;
        patches.push(encodeList(unit, unitValues.items, current, codecs));
        continue;
      }
      if (unitValues.type !== 'fields') continue;
      const patch = withoutEmptyWrites(
        encodeUnit(unit, unitValues.values, current, codecs)
      );
      if (patch !== null) patches.push(patch);
    }
  }
  return patches;
}

/** Create body: an untouched unit contributes nothing (no empty relation lists, no blank columns). */
function withoutEmptyWrites(patch: ResourcePatch): ResourcePatch | null {
  switch (patch.kind) {
    case 'core': {
      const changes = patch.changes.filter(
        change => change.value !== '' && change.value !== null
      );
      return changes.length === 0 ? null : { kind: 'core', changes };
    }
    case 'contact-details':
      return patch.items.length === 0 ? null : patch;
    case 'tags':
    case 'groups':
      return patch.ids.length === 0 && patch.create.length === 0 ? null : patch;
    case 'locations':
      return patch.existingIds.length === 0 && patch.create.length === 0
        ? null
        : patch;
    case 'rates':
      return patch.items.length === 0 ? null : patch;
    case 'custom-field':
      return patch.write.type === 'clear' ? null : patch;
    case 'custom-field-group':
      return patch.rows.length === 0 ? null : patch;
  }
}
